import React, { useState, useEffect, useCallback } from "react";
import { adminApiRequest } from "../utils/adminApi";
import Modal from "./Modal";
import placeholderImage from "../img/placeholder-img.png";
import EditItemModal from "./EditItemModal";
import AddItemModal from "./AddItemModal";

const ItemsTab = () => {
  const [error, setError] = useState(null);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMultipleDeleteModal, setShowMultipleDeleteModal] = useState(false);
  const [showSoldoutModal, setShowSoldoutModal] = useState(false);
  const [showMultipleSoldoutModal, setShowMultipleSoldoutModal] =
    useState(false);
  const [showMultipleAdjustPriceModal, setShowMultipleAdjustPriceModal] =
    useState(false);
  const [showSkippedItemsModal, setShowSkippedItemsModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToSoldout, setItemToSoldout] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoSrc, setPhotoSrc] = useState(placeholderImage);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRequiredOptions, setSelectedRequiredOptions] = useState([]);
  const [newRequiredOptions, setNewRequiredOptions] = useState([]);
  const [newOptionalOptions, setNewOptionalOptions] = useState([]);
  const [selectedOptionalOptions, setSelectedOptionalOptions] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [requiredOptions, setRequiredOptions] = useState([]);
  const [optionalOptions, setOptionalOptions] = useState([]);
  const [itemName, setItemName] = useState("");
  const [itemDescription, setItemDescription] = useState("");
  const [price, setPrice] = useState("");
  const [categories, setCategories] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const itemsPerPage = 50;
  const [searchQuery, setSearchQuery] = useState("");
  const searchResults = filteredItems.filter((item) =>
    item.item_name.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const totalPages = Math.ceil(searchResults.length / itemsPerPage);
  const [currentPage, setCurrentPage] = useState(1);
  const adjustedPage = currentPage > totalPages ? totalPages : currentPage;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = searchResults.slice(indexOfFirstItem, indexOfLastItem);
  const [editingItem, setEditingItem] = useState({
    image: null,
    item_name: "",
    item_description: "",
    price: "",
    category_id: "",
    subcategory_id: "",
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState("amount");
  const [adjustmentDirection, setAdjustmentDirection] = useState("increase");
  const [adjustmentValue, setAdjustmentValue] = useState("");
  const [showPriceAdjustment, setShowPriceAdjustment] = useState(false);
  const [skippedItems, setSkippedItems] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [updatedRequiredOptions, setUpdatedRequiredOptions] = useState([]);
  const [updatedOptionalOptions, setUpdatedOptionalOptions] = useState([]);
  const [removedOptions, setRemovedOptions] = useState([]);

  const fetchMenuItems = useCallback(async (filterValue) => {
    setIsSubmitting(true);
    try {
      const res = await adminApiRequest({
        script: "fetch-instock-items",
        data: {},
      });

      if (!res?.success || !Array.isArray(res.data)) {
        throw new Error(res?.error || res?.message || "Invalid response data");
      }

      setMenuItems(res.data);

      if (filterValue && filterValue !== "all") {
        const selectedCategoryId = parseInt(filterValue, 10);
        const filtered = res.data.filter(
          (item) => Number(item.category_id) === selectedCategoryId,
        );
        setFilteredItems(filtered);
      } else {
        setFilteredItems(res.data);
      }
    } catch (err) {
      console.error("Error fetching menu items:", err?.message);
      setMenuItems([]);
      setFilteredItems([]);
      setErrorWithTimeout(err?.message || "Error fetching menu items");
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const res = await adminApiRequest({
        script: "categories",
        data: {},
      });

      if (!res?.success || !Array.isArray(res.data)) {
        throw new Error(res?.error || "Failed to fetch categories");
      }

      setCategories(res.data);
    } catch (err) {
      console.error("Error fetching categories:", err?.message);
      setCategories([]);
      setErrorWithTimeout(err?.message || "Error fetching categories");
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const fetchOptions = useCallback(async () => {
    try {
      const res = await adminApiRequest({
        script: "fetch-options",
        data: {},
      });

      if (!res?.success) {
        throw new Error(res?.error || "Failed to fetch options");
      }

      const required =
        res?.data?.required_options || res?.required_options || [];
      const optional =
        res?.data?.optional_options || res?.optional_options || [];

      setRequiredOptions(Array.isArray(required) ? required : []);
      setOptionalOptions(Array.isArray(optional) ? optional : []);
    } catch (err) {
      console.error("Error fetching options:", err?.message);
      setRequiredOptions([]);
      setOptionalOptions([]);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchOptions();
  }, [fetchCategories, fetchOptions]);

  useEffect(() => {
    fetchMenuItems(selectedFilter);
  }, [selectedFilter, fetchMenuItems]);

  const setErrorWithTimeout = (errorMessage) => {
    setError(errorMessage);
    if (errorMessage) {
      setTimeout(() => {
        setError("");
      }, 3000);
    }
  };

  const confirmMarkAsSoldout = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!itemToSoldout?.id) {
      setErrorWithTimeout("Invalid item selected for marking as sold out");
      setIsSubmitting(false);
      return;
    }

    try {
      const res = await adminApiRequest({
        script: "soldout-items",
        data: { id: itemToSoldout.id, in_stock: 0 },
      });

      if (res?.success) {
        showSuccess(res?.message || "Item marked as sold out");
      } else {
        setErrorWithTimeout(
          res?.error || res?.message || "Error marking item as sold out",
        );
      }
    } catch (err) {
      setErrorWithTimeout(err?.message || "Error marking item as sold out");
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
        setShowSoldoutModal(false);
        fetchMenuItems(selectedFilter);
      }, 500);
    }
  };

  const confirmMarkSelectedAsSoldout = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await adminApiRequest({
        script: "multiple-soldout-items",
        data: {
          items: selectedItems.map((id) => ({ id, in_stock: 0 })),
        },
      });

      if (res?.success) {
        showSuccess(res?.message || "Items updated");
      } else {
        setErrorWithTimeout(
          res?.error || res?.message || "Failed to mark items as sold out",
        );
      }
    } catch (err) {
      setErrorWithTimeout(
        err?.message || "An error occurred while marking items as sold out",
      );
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
        setShowMultipleSoldoutModal(false);
        setSelectedItems([]);
        fetchMenuItems(selectedFilter);
      }, 500);
    }
  };

  const handleFilterChange = (selectedCategory) => {
    setSelectedFilter(selectedCategory);
    setCurrentPage(1);
    if (selectedCategory === "all") {
      setFilteredItems(menuItems);
    } else {
      const selectedCategoryId = parseInt(selectedCategory, 10);
      const filtered = menuItems.filter(
        (item) => item.category_id === selectedCategoryId,
      );
      setFilteredItems(filtered);
    }
  };

  const handleAddItemClick = () => {
    setShowAddItemModal(true);
    resetFormState();
    fetchOptions();
  };

  const handleCloseModal = () => {
    if (isSubmitting) return;

    setShowAddItemModal(false);
    setShowDeleteModal(false);
    setShowSoldoutModal(false);
    setShowMultipleSoldoutModal(false);
    setShowMultipleDeleteModal(false);
    setShowMultipleAdjustPriceModal(false);
    resetFormState();
  };

  const resetFormState = () => {
    setItemName("");
    setItemDescription("");
    setPrice("");
    setCategoryId("");
    setSubcategoryId("");
    setSelectedRequiredOptions([]);
    setNewRequiredOptions([]);
    setSelectedOptionalOptions([]);
    setNewOptionalOptions([]);

    if (photoSrc && photoSrc !== placeholderImage) {
      URL.revokeObjectURL(photoSrc);
    }

    setPhoto(null);
    setPhotoSrc(placeholderImage);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!itemName || !categoryId) {
      setErrorWithTimeout("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("_method", "POST");
    formData.append("item_name", itemName);
    formData.append("item_description", itemDescription);
    formData.append("price", price || 0.0);
    const selectedCategoryId = subcategoryId ? subcategoryId : categoryId;
    formData.append("category_id", selectedCategoryId);

    formData.append(
      "selected_required_options",
      JSON.stringify(selectedRequiredOptions || []),
    );

    formData.append(
      "selected_optional_options",
      JSON.stringify(selectedOptionalOptions || []),
    );

    formData.append(
      "new_required_options",
      JSON.stringify(newRequiredOptions || []),
    );

    formData.append(
      "new_optional_options",
      JSON.stringify(newOptionalOptions || []),
    );

    if (photo) {
      formData.append("photo", photo);
    }

    try {
      const res = await adminApiRequest({
        script: "add-new-item",
        formData,
      });

      if (!res?.success) {
        throw new Error(
          res?.error || res?.message || "Error adding menu item.",
        );
      }

      handleCloseModal();
      setSelectedFilter("all");
      fetchMenuItems("all");
      showSuccess("Menu item added successfully");
    } catch (error) {
      setErrorWithTimeout(error?.message || "Error adding menu item.");
      console.error("Error adding menu item:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteItem = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleMarkAsSoldout = (item) => {
    setItemToSoldout(item);
    setShowSoldoutModal(true);
  };

  const confirmDeleteItem = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!itemToDelete?.id) {
      setErrorWithTimeout("Invalid item selected for deletion");
      setIsSubmitting(false);
      return;
    }

    try {
      const res = await adminApiRequest({
        script: "delete-item",
        data: { id: itemToDelete.id },
      });

      if (!res?.success) {
        throw new Error(res?.error || res?.message || "Failed");
      }

      showSuccess(res?.message || "Item deleted successfully");
    } catch (err) {
      setErrorWithTimeout(err?.message || "Error deleting item");
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
        handleCloseModal();
        fetchMenuItems(selectedFilter);
      }, 500);
    }
  };

  const confirmDeleteSelectedItems = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await adminApiRequest({
        script: "delete-multiple-items",
        data: { ids: selectedItems },
      });

      if (!res?.success) {
        throw new Error(res?.error || res?.message || "Failed");
      }

      showSuccess(res?.message || "Items deleted");
    } catch (err) {
      setErrorWithTimeout(
        err?.message || "An error occurred while deleting items",
      );
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
        handleCloseModal();
        setSelectedItems([]);
        fetchMenuItems(selectedFilter);
      }, 500);
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  const handleRequiredOptionSelect = (optionId) => {
    if (!optionId) return;

    const selectedOption = requiredOptions.find(
      (option) => String(option.id) === String(optionId),
    );

    if (!selectedOption) return;

    setSelectedRequiredOptions((prev) => {
      if (prev.some((o) => String(o.id) === String(selectedOption.id))) {
        return prev;
      }
      return [...prev, selectedOption];
    });
  };

  const handleOptionalOptionSelect = (optionId) => {
    if (!optionId) return;

    const selectedOption = optionalOptions.find(
      (option) => String(option.id) === String(optionId),
    );

    if (!selectedOption) return;

    setSelectedOptionalOptions((prev) => {
      if (prev.some((o) => String(o.id) === String(selectedOption.id))) {
        return prev;
      }
      return [...prev, selectedOption];
    });
  };

  const handleNewOptionNameChange = (e, index) => {
    const updatedOptions = [...newRequiredOptions];
    updatedOptions[index].option_name = e.target.value;
    setNewRequiredOptions(updatedOptions);
  };

  const handleNewOptionPriceChange = (e, index) => {
    const updatedOptions = [...newRequiredOptions];
    updatedOptions[index].option_price = e.target.value;
    setNewRequiredOptions(updatedOptions);
  };

  const handleRemoveRequiredOption = (index) => {
    const updatedOptions = selectedRequiredOptions.filter(
      (_, i) => i !== index,
    );
    setSelectedRequiredOptions(updatedOptions);
  };

  const handleRemoveNewRequiredOption = (index) => {
    const updatedOptions = newRequiredOptions.filter((_, i) => i !== index);
    setNewRequiredOptions(updatedOptions);
  };

  const handleAddNewRequiredOption = (e) => {
    e.preventDefault();
    setNewRequiredOptions((prevState) => [
      ...prevState,
      {
        option_name: "",
        option_price: "",
        isNew: true,
        option_type: "required",
      },
    ]);
  };

  const handleRemoveOptionalOption = (index) => {
    const updatedOptions = selectedOptionalOptions.filter(
      (_, i) => i !== index,
    );
    setSelectedOptionalOptions(updatedOptions);
  };

  const handleAddNewOptionalOption = (e) => {
    e.preventDefault();
    setNewOptionalOptions((prevState) => [
      ...prevState,
      {
        option_name: "",
        option_price: "",
        isNew: true,
        option_type: "optional",
      },
    ]);
  };

  const handleNewOptionalOptionNameChange = (e, index) => {
    const updatedOptions = [...newOptionalOptions];
    updatedOptions[index].option_name = e.target.value;
    setNewOptionalOptions(updatedOptions);
  };

  const handleNewOptionalOptionPriceChange = (e, index) => {
    const updatedOptions = [...newOptionalOptions];
    updatedOptions[index].option_price = e.target.value;
    setNewOptionalOptions(updatedOptions);
  };

  const handleRemoveNewOptionalOption = (index) => {
    const updatedOptions = newOptionalOptions.filter((_, i) => i !== index);
    setNewOptionalOptions(updatedOptions);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    const maxSize = 5 * 1024 * 1024;

    if (file) {
      if (file.size > maxSize) {
        setErrorWithTimeout("File size exceeds the 5MB limit");
        e.target.value = "";
        return;
      }

      if (photoSrc && photoSrc !== placeholderImage) {
        URL.revokeObjectURL(photoSrc);
      }

      const newPhotoSrc = URL.createObjectURL(file);
      setPhotoSrc(newPhotoSrc);
      setPhoto(file);
    }
  };

  const handleRemovePhoto = () => {
    if (photoSrc && photoSrc !== placeholderImage) {
      URL.revokeObjectURL(photoSrc);
    }

    setPhoto(null);
    setPhotoSrc(placeholderImage);
    const fileInput = document.getElementById("fileInput");
    if (fileInput) fileInput.value = "";
  };

  const handleEditClick = (item) => {
    let parentCategory = null;
    let subcategory = null;

    categories.forEach((cat) => {
      cat.subcategories.forEach((sub) => {
        if (parseInt(sub.id, 10) === parseInt(item.category_id, 10)) {
          parentCategory = cat;
          subcategory = sub;
        }
      });
    });

    setEditingItem({
      ...item,
      category_id: parentCategory ? parentCategory.id : item.category_id,
      subcategory_id: subcategory ? subcategory.id : "",
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (!editingItem.item_name.trim() || !editingItem.category_id) {
      setErrorWithTimeout("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("id", editingItem.id);
    formData.append("item_name", editingItem.item_name.trim());
    formData.append("item_description", editingItem.item_description || "");
    formData.append("price", editingItem.price || 0.0);
    formData.append(
      "category_id",
      editingItem.subcategory_id || editingItem.category_id,
    );

    if (editingItem?.file) {
      formData.append("photo", editingItem.file);
    } else {
      formData.append(
        "photo_url",
        editingItem.image && typeof editingItem.image === "string"
          ? editingItem.image
          : "",
      );
    }

    const optionsPayload = {
      selected: {
        required: updatedRequiredOptions.map((opt) => ({
          id: opt.id || null,
          option_name: opt.option_name,
          option_price: opt.option_price,
          option_type: "required",
        })),
        optional: updatedOptionalOptions.map((opt) => ({
          id: opt.id || null,
          option_name: opt.option_name,
          option_price: opt.option_price,
          option_type: "optional",
        })),
      },
      removed: removedOptions,
    };

    formData.append("options_data", JSON.stringify(optionsPayload));

    try {
      const res = await adminApiRequest({
        script: "edit-item",
        formData,
      });

      if (!res?.success) {
        throw new Error(res?.error || res?.message || "Failed");
      }

      setIsSubmitting(false);
      handleCloseEditModal();
      fetchMenuItems(selectedFilter);
      showSuccess("Menu item updated successfully");
    } catch (err) {
      console.error("handleEditSubmit:", err);
      setErrorWithTimeout(err?.message || "Error updating menu item.");
      setIsSubmitting(false);
    }
  };

  const resetEditingItem = () => {
    setEditingItem({
      item_name: "",
      item_description: "",
      price: "",
      category_id: "",
      image: null,
    });

    setUpdatedRequiredOptions([]);
    setUpdatedOptionalOptions([]);
    setRemovedOptions([]);
  };

  const handleCloseEditModal = () => {
    if (isSubmitting) return;
    setShowEditModal(false);
    resetEditingItem();
  };

  const editFormHandlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const newPhotoSrc = reader.result;

        setEditingItem((prev) => ({
          ...prev,
          image: newPhotoSrc,
          file,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const editFormHandleRemovePhoto = () => {
    if (
      typeof editingItem.image === "object" &&
      editingItem.image !== placeholderImage
    ) {
      URL.revokeObjectURL(editingItem.image);
    }

    setEditingItem((prev) => ({
      ...prev,
      image: null,
    }));

    const fileInput = document.getElementById("fileInput");
    if (fileInput) fileInput.value = "";
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const toggleBulkEdit = () => {
    setShowBulkEdit(!showBulkEdit);
    setShowPriceAdjustment(false);
    setSelectedItems([]);
  };

  const handleSelect = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id)
        ? prev.filter((itemId) => itemId !== id)
        : [...prev, id],
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(currentItems.map((item) => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  const confirmPriceAdjustment = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await adminApiRequest({
        script: "adjust-prices",
        data: {
          selectedItems,
          adjustmentType,
          adjustmentDirection,
          adjustmentValue: parseFloat(adjustmentValue),
        },
      });

      if (!res?.success) {
        throw new Error(res?.error || res?.message || "Failed");
      }

      showSuccess(res?.message || "Prices adjusted successfully");

      if (Array.isArray(res.skippedItems) && res.skippedItems.length > 0) {
        setSkippedItems(res.skippedItems);
        setShowSkippedItemsModal(true);
      }
    } catch (err) {
      setErrorWithTimeout(
        err?.message || "An error occurred while adjusting prices",
      );
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
        setSelectedItems([]);
        setAdjustmentType("amount");
        setAdjustmentDirection("increase");
        setAdjustmentValue("");
        setShowMultipleAdjustPriceModal(false);
        setShowPriceAdjustment(false);
        fetchMenuItems(selectedFilter);
      }, 500);
    }
  };

  const togglePriceAdjustment = () => {
    if (showPriceAdjustment) {
      setAdjustmentType("amount");
      setAdjustmentDirection("increase");
      setAdjustmentValue("");
    }
    setShowPriceAdjustment(!showPriceAdjustment);
  };

  const sortedCategories = [...categories].sort((a, b) => {
    const priority = {
      1: 1,
      17: 2,
      16: 99,
    };

    const aPriority = priority[a.id] || 50;
    const bPriority = priority[b.id] || 50;

    return aPriority - bPriority;
  });

  const inputClass =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-200";

  return (
    <main className="h-full overflow-y-auto">
      {showSuccessMessage && (
        <div className="fixed right-4 top-4 z-50 rounded-xl border border-emerald-200 bg-emerald-50 px-2 py-3 text-sm font-medium text-emerald-700 shadow-lg">
          {successMessage}
        </div>
      )}

      <div className="w-full">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-2 py-4 md:px-4">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <h1 className="text-xl font-semibold tracking-tight text-slate-900 md:text-2xl">
                  Menu Items
                </h1>
              </div>

              <div className="flex w-full flex-col gap-3 md:flex-row xl:w-auto">
                <div className="min-w-[220px]">
                  <select
                    id="kid-status-filter"
                    className={inputClass}
                    value={selectedFilter}
                    onChange={(e) => handleFilterChange(e.target.value)}
                  >
                    <option value="all">All Items</option>

                    {(sortedCategories || []).map((category) => (
                      <React.Fragment key={category.id}>
                        <option value={category.id}>
                          {category.category_name}
                        </option>

                        {(category.subcategories || []).map((subcat) => (
                          <option key={subcat.id} value={subcat.id}>
                            - {subcat.category_name}
                          </option>
                        ))}
                      </React.Fragment>
                    ))}
                  </select>
                </div>

                <div className="w-full xl:w-[320px]">
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className={inputClass}
                  />
                </div>

                <button
                  onClick={toggleBulkEdit}
                  className={`inline-flex items-center justify-center rounded-2xl px-5 py-3 text-sm font-semibold transition ${
                    showBulkEdit
                      ? "bg-rose-600 text-white hover:bg-rose-700"
                      : "bg-slate-900 text-white hover:bg-slate-800"
                  }`}
                >
                  {showBulkEdit ? "Close Bulk Edit" : "Bulk Edit"}
                </button>

                <button
                  className="inline-flex items-center justify-center rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700"
                  onClick={handleAddItemClick}
                >
                  Add Item
                </button>
              </div>
            </div>
          </div>

          {showBulkEdit && (
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-4 md:px-6">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300"
                        onChange={handleSelectAll}
                        checked={
                          selectedItems.length === currentItems.length &&
                          currentItems.length > 0
                        }
                      />
                      <span className="font-medium">Select All</span>
                    </label>

                    <span className="text-sm font-medium text-slate-600">
                      {selectedItems.length} item(s) selected
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      className="rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600"
                      onClick={() => {
                        if (selectedItems.length === 0) {
                          setErrorWithTimeout(
                            "Please select at least one item.",
                          );
                          return;
                        }
                        setShowMultipleSoldoutModal(true);
                      }}
                    >
                      Mark as Sold Out
                    </button>

                    <button
                      className="rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700"
                      onClick={() => {
                        if (selectedItems.length === 0) {
                          setErrorWithTimeout(
                            "Please select at least one item.",
                          );
                          return;
                        }
                        setShowMultipleDeleteModal(true);
                      }}
                    >
                      Delete Selected
                    </button>

                    <button
                      className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
                      onClick={() => {
                        if (selectedItems.length === 0) {
                          setErrorWithTimeout(
                            "Please select at least one item.",
                          );
                          return;
                        }
                        togglePriceAdjustment();
                      }}
                    >
                      Adjust Price
                    </button>
                  </div>
                </div>

                {showPriceAdjustment && (
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="flex flex-col gap-4">
                      <p className="text-sm font-semibold text-slate-900">
                        Price Adjustment
                      </p>

                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="radio"
                            name="adjustmentDirection"
                            value="increase"
                            checked={adjustmentDirection === "increase"}
                            onChange={(e) =>
                              setAdjustmentDirection(e.target.value)
                            }
                          />
                          Increase
                        </label>

                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="radio"
                            name="adjustmentDirection"
                            value="decrease"
                            checked={adjustmentDirection === "decrease"}
                            onChange={(e) =>
                              setAdjustmentDirection(e.target.value)
                            }
                          />
                          Decrease
                        </label>

                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="radio"
                            name="adjustmentType"
                            value="amount"
                            checked={adjustmentType === "amount"}
                            onChange={(e) => setAdjustmentType(e.target.value)}
                          />
                          By Amount
                        </label>

                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="radio"
                            name="adjustmentType"
                            value="percentage"
                            checked={adjustmentType === "percentage"}
                            onChange={(e) => setAdjustmentType(e.target.value)}
                          />
                          By Percentage
                        </label>
                      </div>

                      <div className="flex flex-col gap-3 sm:flex-row">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            placeholder={`Enter ${
                              adjustmentType === "amount"
                                ? "Amount"
                                : "Percentage"
                            }`}
                            value={adjustmentValue}
                            onChange={(e) => setAdjustmentValue(e.target.value)}
                            className={`${inputClass} pr-12`}
                          />
                          <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-500">
                            {adjustmentType === "amount" ? "£" : "%"}
                          </span>
                        </div>

                        <button
                          className="rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700"
                          onClick={() => {
                            setShowMultipleAdjustPriceModal(true);
                          }}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="p-4 md:p-6">
            {currentItems.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-sm text-slate-500">
                No items found
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
                {currentItems
                  .slice()
                  .sort((a, b) => a.item_name.localeCompare(b.item_name))
                  .map((item, index) => (
                    <div
                      key={item.id}
                      className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md"
                    >
                      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start gap-3">
                            {showBulkEdit && (
                              <input
                                type="checkbox"
                                className="mt-3 h-4 w-4 rounded border-slate-300"
                                checked={selectedItems.includes(item.id)}
                                onChange={() => handleSelect(item.id)}
                              />
                            )}

                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-sm font-semibold text-slate-700">
                              {(adjustedPage - 1) * itemsPerPage + index + 1}
                            </div>

                            {item.image ? (
                              <img
                                src={`https://lasgidirestaurant.com/${item.image}`}
                                alt={item.item_name}
                                className="h-16 w-16 shrink-0 rounded-2xl border border-slate-200 object-cover"
                              />
                            ) : (
                              <img
                                src={placeholderImage}
                                alt="Placeholder"
                                className="h-16 w-16 shrink-0 rounded-2xl border border-slate-200 object-cover"
                              />
                            )}

                            <div className="min-w-0 flex-1 max-[479px]:hidden">
                              <p className="break-words text-sm font-semibold leading-5 text-slate-900 md:text-base">
                                {item.item_name}
                              </p>

                              {item.price !== "0.00" && (
                                <p className="mt-1 text-sm font-medium text-slate-500">
                                  £{item.price}
                                </p>
                              )}

                              <div className="mt-2">
                                <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-600">
                                  In Stock
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="mt-3 min-w-0 hidden max-[479px]:block">
                            <p className="break-words text-sm font-semibold leading-5 text-slate-900">
                              {item.item_name}
                            </p>

                            {item.price !== "0.00" && (
                              <p className="mt-1 text-sm font-medium text-slate-500">
                                £{item.price}
                              </p>
                            )}

                            <div className="mt-2">
                              <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-600">
                                In Stock
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 xl:flex-nowrap xl:shrink-0">
                          <button
                            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
                            onClick={() => handleEditClick(item)}
                          >
                            <i className="fas fa-pencil-alt text-sm"></i>
                          </button>

                          <button
                            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-amber-200 text-amber-600 transition hover:bg-amber-50"
                            onClick={() => handleMarkAsSoldout(item)}
                          >
                            <i className="fas fa-ban text-sm"></i>
                          </button>

                          <button
                            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-rose-200 text-rose-600 transition hover:bg-rose-50"
                            onClick={() => handleDeleteItem(item)}
                          >
                            <i className="fas fa-trash-alt text-sm"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {totalPages > 0 && (
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {Array.from({ length: totalPages }, (_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => handlePageChange(index + 1)}
                    className={`min-w-[42px] rounded-xl px-4 py-2 text-sm font-semibold transition ${
                      adjustedPage === index + 1
                        ? "bg-slate-900 text-white"
                        : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 px-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl border border-rose-200 bg-white p-5 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-100 text-rose-600">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Error</p>
                <p className="mt-1 text-sm text-slate-600">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {isSubmitting && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-white/60 backdrop-blur-sm">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-slate-900"></div>
        </div>
      )}

      <EditItemModal
        showEditModal={showEditModal}
        handleCloseEditModal={handleCloseEditModal}
        handleEditSubmit={handleEditSubmit}
        editingItem={editingItem}
        setEditingItem={setEditingItem}
        categories={categories}
        editFormHandlePhotoChange={editFormHandlePhotoChange}
        editFormHandleRemovePhoto={editFormHandleRemovePhoto}
        placeholderImage={placeholderImage}
        removedOptions={removedOptions}
        setRemovedOptions={setRemovedOptions}
        updatedRequiredOptions={updatedRequiredOptions}
        setUpdatedRequiredOptions={setUpdatedRequiredOptions}
        updatedOptionalOptions={updatedOptionalOptions}
        setUpdatedOptionalOptions={setUpdatedOptionalOptions}
        isSubmitting={isSubmitting}
      />

      <AddItemModal
        showAddItemModal={showAddItemModal}
        handleCloseModal={handleCloseModal}
        handleAddSubmit={handleAddSubmit}
        handlePhotoChange={handlePhotoChange}
        handleRemovePhoto={handleRemovePhoto}
        photo={photo}
        photoSrc={photoSrc}
        itemName={itemName}
        setItemName={setItemName}
        itemDescription={itemDescription}
        setItemDescription={setItemDescription}
        price={price}
        setPrice={setPrice}
        categories={categories}
        categoryId={categoryId}
        setCategoryId={setCategoryId}
        subcategoryId={subcategoryId}
        setSubcategoryId={setSubcategoryId}
        requiredOptions={requiredOptions}
        optionalOptions={optionalOptions}
        selectedRequiredOptions={selectedRequiredOptions}
        handleRequiredOptionSelect={handleRequiredOptionSelect}
        handleRemoveRequiredOption={handleRemoveRequiredOption}
        newRequiredOptions={newRequiredOptions}
        handleNewOptionNameChange={handleNewOptionNameChange}
        handleNewOptionPriceChange={handleNewOptionPriceChange}
        handleRemoveNewRequiredOption={handleRemoveNewRequiredOption}
        handleAddNewRequiredOption={handleAddNewRequiredOption}
        selectedOptionalOptions={selectedOptionalOptions}
        handleOptionalOptionSelect={handleOptionalOptionSelect}
        handleRemoveOptionalOption={handleRemoveOptionalOption}
        newOptionalOptions={newOptionalOptions}
        handleNewOptionalOptionNameChange={handleNewOptionalOptionNameChange}
        handleNewOptionalOptionPriceChange={handleNewOptionalOptionPriceChange}
        handleRemoveNewOptionalOption={handleRemoveNewOptionalOption}
        handleAddNewOptionalOption={handleAddNewOptionalOption}
        addingItem={isSubmitting}
      />

      {showSkippedItemsModal && (
        <Modal
          title="Price Adjustment Successful"
          onClose={() => setShowSkippedItemsModal(false)}
          showCancel={false}
          showSubmitButton={false}
          className="skipped-items-modal"
        >
          <div className="space-y-4 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-4">
            <p className="text-sm font-medium text-amber-800">
              Prices adjusted successfully, but the following items could not be
              adjusted to avoid negative prices:
            </p>
            <ul className="list-disc space-y-1 pl-5 text-sm text-amber-700">
              {skippedItems.map((itemName, index) => (
                <li key={index}>{itemName}</li>
              ))}
            </ul>
          </div>
        </Modal>
      )}

      {showDeleteModal && (
        <Modal
          title="Delete Item"
          onClose={handleCloseModal}
          onSubmit={confirmDeleteItem}
          submitButtonText={isSubmitting ? "Deleting..." : "Delete Item"}
          showCancel={true}
          showSubmitButton={true}
          className="delete-modal-content"
          disableSubmit={isSubmitting}
        >
          <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-4">
            <p className="text-sm font-medium text-rose-700">
              Are you sure you want to permanently delete this item from the
              database?
            </p>
          </div>
        </Modal>
      )}

      {showSoldoutModal && (
        <Modal
          title="Mark Item as Soldout"
          onClose={handleCloseModal}
          onSubmit={confirmMarkAsSoldout}
          submitButtonText={isSubmitting ? "Updating..." : "Mark as Soldout"}
          showCancel={true}
          showSubmitButton={true}
          className="delete-modal-content"
          disableSubmit={isSubmitting}
        >
          <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-4">
            <p className="text-sm font-medium text-amber-800">
              Are you sure you want to mark this item as sold out?
            </p>
          </div>
        </Modal>
      )}

      {showMultipleSoldoutModal && (
        <Modal
          title="Mark Selected Items as Soldout"
          onClose={handleCloseModal}
          onSubmit={confirmMarkSelectedAsSoldout}
          submitButtonText={isSubmitting ? "Updating..." : "Mark as Soldout"}
          showCancel={true}
          showSubmitButton={true}
          className="delete-modal-content"
          disableSubmit={isSubmitting}
        >
          <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-4">
            <p className="text-sm font-medium text-amber-800">
              Are you sure you want to mark the selected items as sold out?
            </p>
          </div>
        </Modal>
      )}

      {showMultipleDeleteModal && (
        <Modal
          title="Delete Items"
          onClose={handleCloseModal}
          onSubmit={confirmDeleteSelectedItems}
          submitButtonText={isSubmitting ? "Deleting..." : "Delete Items"}
          showCancel={true}
          showSubmitButton={true}
          className="delete-modal-content"
          disableSubmit={isSubmitting}
        >
          <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-4">
            <p className="text-sm font-medium text-rose-700">
              Are you sure you want to permanently delete the selected items
              from the database?
            </p>
          </div>
        </Modal>
      )}

      {showMultipleAdjustPriceModal && (
        <Modal
          title="Adjust Prices"
          onClose={handleCloseModal}
          onSubmit={confirmPriceAdjustment}
          submitButtonText={isSubmitting ? "Applying..." : "Adjust Prices"}
          showCancel={true}
          showSubmitButton={true}
          className="delete-modal-content"
          disableSubmit={isSubmitting}
        >
          <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-4">
            <p className="text-sm font-medium text-amber-800">
              Are you sure you want to adjust the prices of the selected items?
            </p>
          </div>
        </Modal>
      )}
    </main>
  );
};

export default ItemsTab;
